import React, { useState, useEffect } from 'react';
import { User } from '../../types';
import { useToast } from '../ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Users, 
  Eye, 
  FileSpreadsheet,
  LogOut,
  RefreshCw,
  Loader2,
  Activity
} from 'lucide-react';

// Import tab components
import { TeamsOverviewTab } from './tabs/TeamsOverviewTab';
import { SupervisorsTab } from './tabs/SupervisorsTab';
import { ReportsAnalyticsTab } from './tabs/ReportsAnalyticsTab';

interface RefactoredMonitorDashboardProps {
  currentUser: User;
  onLogout: () => void;
}

export const RefactoredMonitorDashboard: React.FC<RefactoredMonitorDashboardProps> = ({
  currentUser,
  onLogout
}) => {
  const [activeTab, setActiveTab] = useState('teams');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      // Trigger refresh in active tab
      window.dispatchEvent(new CustomEvent('monitor-refresh'));
      toast({
        title: 'Actualizat',
        description: 'Datele au fost actualizate',
      });
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut actualiza datele',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">
                Dashboard Monitor
              </h1>
              <Badge variant="secondary" className="text-sm">
                <Eye className="h-3 w-3 mr-1" />
                {currentUser.name}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Actualizează
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
              >
                <LogOut className="h-4 w-4" />
                Deconectare
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-2xl mx-auto">
            <TabsTrigger value="teams" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Echipe</span>
              <span className="sm:hidden">Echipe</span>
            </TabsTrigger>
            
            <TabsTrigger value="supervisors" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Supervisori</span>
              <span className="sm:hidden">Supervisori</span>
            </TabsTrigger>
            
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              <span className="hidden sm:inline">Rapoarte</span>
              <span className="sm:hidden">Rapoarte</span>
            </TabsTrigger>
          </TabsList>

          {/* Teams Overview Tab */}
          <TabsContent value="teams" className="space-y-4">
            <TeamsOverviewTab
              monitorId={currentUser.id}
            />
          </TabsContent>

          {/* Supervisors Tab */}
          <TabsContent value="supervisors" className="space-y-4">
            <SupervisorsTab
              monitorId={currentUser.id}
            />
          </TabsContent>

          {/* Reports & Analytics Tab */}
          <TabsContent value="reports" className="space-y-4">
            <ReportsAnalyticsTab
              monitorId={currentUser.id}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};